// /oauth/consent: connecting Claude, ChatGPT or Claude Code to Hyprflow.
//
// A step of sign-in, on the sign-in card (owner, 2026-10-14): GoTrue sends the
// person here with ?authorization_id=…, App shows the normal password-and-code
// sign-in first if there is no session, then this.
//
// Order matters. mcp_consent() goes FIRST: it records that a full session
// opened this request, and the auth gate refuses any connector token without
// that record (20261014140000_mcp_client.sql). getAuthorizationDetails comes
// second because, for an app approved once before, it approves again on its
// own and the code is already on its way back to the app.
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Icon, type IconName } from '../components/primitives';
import { AuthCard, AuthNote, AuthShell } from '../components/auth-ui';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';

type State =
  | { step: 'checking' }
  | { step: 'ask'; app: string; host: string }
  | { step: 'leaving' }
  | { step: 'refused'; message: string };

const EXPIRED = 'This connection request has expired. Start again from the app.';

// Where the code goes, in words: a loopback redirect is Claude Code on this computer.
const hostOf = (uri: string) => {
  try {
    const { host, hostname } = new URL(uri);
    return hostname === 'localhost' || hostname === '127.0.0.1' ? `this computer (${host})` : host;
  } catch {
    return uri;
  }
};

const Point = ({ icon, children }: { icon: IconName; children: string }) => (
  <li style={{ display: 'flex', gap: 'var(--hf-space-3)', alignItems: 'flex-start' }}>
    <Icon name={icon} size={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--hf-ink-3)' }} />
    <span>{children}</span>
  </li>
);

/** `go` leaves for the app; a test passes its own. */
export function ConsentScreen({
  go = (url: string) => window.location.assign(url),
}: {
  go?: (url: string) => void;
}) {
  const { session, signOut } = useAuth();
  const id = new URLSearchParams(useLocation().search).get('authorization_id') ?? '';
  const [state, setState] = useState<State>({ step: 'checking' });
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null);
  // Once per page: StrictMode runs effects twice, and a second details call
  // after an auto-approve finds the request already used.
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !supabase) return;
    started.current = true;
    const sb = supabase;
    void (async () => {
      if (!id) return setState({ step: 'refused', message: EXPIRED });
      const consent = await sb.rpc('mcp_consent', { p_authorization_id: id });
      if (consent.error) return setState({ step: 'refused', message: consent.error.message });
      const { data, error } = await sb.auth.oauth.getAuthorizationDetails(id);
      if (error || !data) return setState({ step: 'refused', message: EXPIRED });
      if ('redirect_url' in data) {
        setState({ step: 'leaving' });
        return go(data.redirect_url);
      }
      setState({ step: 'ask', app: data.client.name || 'An app', host: hostOf(data.redirect_uri) });
    })();
  }, [id, go]);

  const decide = async (allow: boolean) => {
    if (!supabase || busy) return;
    setBusy(allow ? 'allow' : 'deny');
    const opts = { skipBrowserRedirect: true };
    const { data, error } = allow
      ? await supabase.auth.oauth.approveAuthorization(id, opts)
      : await supabase.auth.oauth.denyAuthorization(id, opts);
    if (error || !data) {
      setBusy(null);
      return setState({ step: 'refused', message: EXPIRED });
    }
    setState({ step: 'leaving' });
    go(data.redirect_url);
  };

  if (state.step === 'checking' || state.step === 'leaving') {
    return (
      <AuthShell>
        <AuthCard
          title={state.step === 'checking' ? 'Connecting an app' : 'Taking you back to the app'}
          sub={
            state.step === 'checking'
              ? 'Checking the request…'
              : 'You can close this tab once it opens.'
          }
        >
          {null}
        </AuthCard>
      </AuthShell>
    );
  }

  if (state.step === 'refused') {
    return (
      <AuthShell>
        <AuthCard title="This app can’t connect" sub={state.message}>
          <Button variant="secondary" full onClick={() => go('/')}>
            Go to Hyprflow
          </Button>
        </AuthCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthCard
        title={`Connect ${state.app}?`}
        sub={
          <>
            {state.app} will read Hyprflow as you
            {session?.user.email ? `, ${session.user.email}` : ''}, and send you back to{' '}
            <strong style={{ color: 'var(--hf-ink-2)' }}>{state.host}</strong>.
          </>
        }
      >
        <ul
          style={{
            listStyle: 'none',
            margin: '0 0 var(--hf-space-5)',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-3)',
            fontSize: 'var(--hf-text-md)',
            lineHeight: 'var(--hf-leading-body)',
            color: 'var(--hf-ink-2)',
          }}
        >
          <Point icon="lock">Read-only: it can’t change anything in Hyprflow.</Point>
          <Point icon="shield">
            No pay or project ₹: salaries, bank details, payroll and budgets stay out, and so do
            leave reasons.
          </Point>
          <Point icon="check">
            It reads your time, leave, projects, tasks and capacity, as you see them here.
          </Point>
        </ul>
        <Button
          variant="primary"
          size="xl"
          full
          loading={busy === 'allow'}
          onClick={() => void decide(true)}
        >
          Allow
        </Button>
        <Button
          variant="ghost"
          full
          loading={busy === 'deny'}
          onClick={() => void decide(false)}
          style={{ marginTop: 'var(--hf-space-2)' }}
        >
          Deny
        </Button>
        <AuthNote
          style={{
            marginTop: 'var(--hf-space-4)',
            fontSize: 'var(--hf-text-xs)',
            textAlign: 'center',
          }}
        >
          What it reads goes to that app’s AI provider. Not you?{' '}
          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            Sign out
          </Button>
        </AuthNote>
      </AuthCard>
    </AuthShell>
  );
}
